import { IBiePlanningContext } from './bieprofile.models';
import {
  fetchBiePlanningContext,
  setBieHistoricalDataset,
  setBiePlanningCycle,
  setBieScenario,
} from './bieprofile.apis';

export type PlanningContextSetter = (context: IBiePlanningContext) => void;

/**
 * Context-aware facade over bieprofile.apis.
 *
 * The API module remains transport-only. This service updates PageRoot state
 * after every successful profile/context call, then returns the same context
 * to the caller.
 */
export class BieProfileService {
  public constructor(private readonly updateContext: PlanningContextSetter) {}

  private commit = (context: IBiePlanningContext): IBiePlanningContext => {
    this.updateContext(context);
    return context;
  };

  public getContext = async (): Promise<IBiePlanningContext> =>
    this.commit(await fetchBiePlanningContext());

  public setPlanningCycle = async (
    planningCycleId: number,
  ): Promise<IBiePlanningContext> =>
    this.commit(await setBiePlanningCycle(planningCycleId));

  public setHistoricalDataset = async (
    historicalDatasetId: number,
  ): Promise<IBiePlanningContext> =>
    this.commit(await setBieHistoricalDataset(historicalDatasetId));

  public setScenario = async (
    scenarioId: number,
  ): Promise<IBiePlanningContext> =>
    this.commit(await setBieScenario(scenarioId));
}

/** Functional helpers for pages that do not retain a service instance. */
export const loadPlanningContext = async (
  updateContext: PlanningContextSetter,
): Promise<IBiePlanningContext> => {
  const context = await fetchBiePlanningContext();
  updateContext(context);
  return context;
};

export const usePlanningCycle = async (
  planningCycleId: number,
  updateContext: PlanningContextSetter,
): Promise<IBiePlanningContext> => {
  const context = await setBiePlanningCycle(planningCycleId);
  updateContext(context);
  return context;
};

export const useHistoricalDataset = async (
  historicalDatasetId: number,
  updateContext: PlanningContextSetter,
): Promise<IBiePlanningContext> => {
  const context = await setBieHistoricalDataset(historicalDatasetId);
  updateContext(context);
  return context;
};

export const useScenario = async (
  scenarioId: number,
  updateContext: PlanningContextSetter,
): Promise<IBiePlanningContext> => {
  const context = await setBieScenario(scenarioId);
  updateContext(context);
  return context;
};
